import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Search,
  Download,
  Zap,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Truck,
  User,
  Timer,
  ChevronRight,
  Play,
  RefreshCw,
  Loader2 } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from
"@/shared/components/ui/dialog";
import { Progress } from "@/shared/components/ui/progress";
import { cn } from '@/shared/utils';
import { toast } from 'sonner';

const API_URL = API_BASE;

// Status and priority configs remain the same
const rushStatuses = [
{ value: 'RECEIVED', label: 'Received' },
{ value: 'ALLOCATED', label: 'Allocated' },
{ value: 'PICKING', label: 'Picking' },
{ value: 'PICKED', label: 'Picked' },
{ value: 'PACKING', label: 'Packing' },
{ value: 'PACKED', label: 'Packed' },
{ value: 'DISPATCHED', label: 'Dispatched' },
{ value: 'DELIVERED', label: 'Delivered' }];


const rushPriorities = [
{ value: 'SAME_DAY', label: 'Same Day' },
{ value: 'NEXT_DAY_AM', label: 'Next Day AM' },
{ value: 'NEXT_DAY', label: 'Next Day' },
{ value: 'EXPRESS_48H', label: '48H Express' }];


const statusConfig = {
  RECEIVED: { color: 'bg-red-100 text-red-700 border-red-200' },
  ALLOCATED: { color: 'bg-orange-100 text-orange-700 border-orange-200' },
  PICKING: { color: 'bg-amber-100 text-amber-700 border-amber-200' },
  PICKED: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  PACKING: { color: 'bg-lime-100 text-lime-700 border-lime-200' },
  PACKED: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  DISPATCHED: { color: 'bg-blue-100 text-blue-700 border-blue-200' },
  DELIVERED: { color: 'bg-green-100 text-green-700 border-green-200' }
};

const priorityConfig = {
  SAME_DAY: { color: 'bg-red-500 text-white', label: 'SAME DAY', urgent: true },
  NEXT_DAY_AM: { color: 'bg-orange-500 text-white', label: 'NEXT DAY AM', urgent: true },
  NEXT_DAY: { color: 'bg-amber-500 text-white', label: 'NEXT DAY', urgent: false },
  EXPRESS_48H: { color: 'bg-yellow-500 text-white', label: '48H EXPRESS', urgent: false }
};

const RushCard = ({ order, onView }) => {const { t } = useTranslation("wms");
  const statusLabel = rushStatuses.find((s) => s.value === order.status)?.label || order.status;
  const priority = priorityConfig[order.priority] || priorityConfig.EXPRESS_48H;
  const [timeLeft, setTimeLeft] = useState(order.slaRemaining);

  // Calculate progress percentage through workflow
  const statusOrder = ['RECEIVED', 'ALLOCATED', 'PICKING', 'PICKED', 'PACKING', 'PACKED', 'DISPATCHED', 'DELIVERED'];
  const currentIdx = statusOrder.indexOf(order.status);
  const progress = (currentIdx + 1) / statusOrder.length * 100;

  return (
    <div
      className={cn(
        "bg-white rounded-xl border-2 p-5 hover:shadow-md transition-all cursor-pointer",
        priority.urgent ? "border-red-300" : "border-gray-200"
      )}
      onClick={() => onView(order)}
      data-testid={`rush-card-${order.id}`}>
      
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center",
            priority.urgent ? "bg-red-100" : "bg-amber-100"
          )}>
            <Zap className={cn(
              "h-5 w-5",
              priority.urgent ? "text-red-600" : "text-amber-600"
            )} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{order.id}</span>
              <Badge className={cn("text-xs font-bold", priority.color)}>
                {priority.label}
              </Badge>
            </div>
            <div className="text-sm text-gray-600 mt-0.5">{order.customer}</div>
          </div>
        </div>
        <Badge variant="outline" className={cn(statusConfig[order.status]?.color)}>
          {statusLabel}
        </Badge>
      </div>

      {/* SLA Timer */}
      <div className={cn(
        "rounded-lg p-3 mb-4",
        priority.urgent && order.status !== 'DELIVERED' ? "bg-red-50 border border-red-200" : "bg-gray-50"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Timer className={cn("h-4 w-4", priority.urgent ? "text-red-600" : "text-gray-600")} />
            <span className="text-sm font-medium">{t("slaDeadline", "SLA Deadline")}</span>
          </div>
          <div className={cn(
            "text-lg font-bold",
            priority.urgent && order.status !== 'DELIVERED' ? "text-red-600" : "text-gray-900"
          )}>
            {order.slaRemaining}
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {new Date(order.slaDeadline).toLocaleString()}
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>{t("progress", "Progress")}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">{t("items", "Items")}</span>
          <div className="font-medium text-gray-900">{order.totalItems} ({order.totalQty}{t("units", "units)")}</div>
        </div>
        <div>
          <span className="text-gray-500">{t("assignedTo", "Assigned To")}</span>
          <div className="font-medium text-gray-900">{order.assignedPicker || 'Unassigned'}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="text-sm text-gray-600 truncate max-w-[200px]">
          {order.reason}
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>

      {order.trackingId &&
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-blue-600">
          <Truck className="h-4 w-4" />
          <span>{t("tracking", "Tracking:")}{order.trackingId}</span>
        </div>
      }
    </div>);

};

export const RushOrders = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRushOrders = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/special-orders/?order_type=rush`);
        if (response.ok) {
          const data = await response.json();
          // Transform API data to component format
          const transformedOrders = data.map((order) => ({
            id: order.special_order_number || order.id,
            salesOrder: order.sales_order_ref || order.special_order_number,
            customer: order.customer || 'N/A',
            customerId: order.customer,
            priority: order.rush_priority?.toUpperCase()?.replace(/[\s-]/g, '_') || 'EXPRESS_48H',
            status: order.status?.toUpperCase() || 'RECEIVED',
            createdAt: order.created_at,
            slaDeadline: order.rush_cutoff || order.expected_ship_date,
            slaRemaining: calculateSlaRemaining(order.rush_cutoff || order.expected_ship_date),
            assignedPicker: order.assignee,
            totalItems: order.items?.length || 0,
            totalQty: order.items?.reduce((sum, i) => sum + (i.quantity || 0), 0) || 0,
            items: order.items?.map((item, idx) => ({
              id: idx + 1,
              sku: item.sku,
              name: item.product_name || item.sku,
              qty: item.quantity,
              location: item.location || 'TBD',
              picked: item.picked_quantity || 0
            })) || [],
            reason: order.notes || 'Rush order',
            trackingId: order.tracking_number
          }));
          setOrders(transformedOrders);
        }
      } catch (err) {
        console.error('Failed to fetch rush orders:', err);
        toast.error('Failed to load rush orders');
      } finally {
        setLoading(false);
      }
    };

    fetchRushOrders();
  }, []);

  const calculateSlaRemaining = (deadline) => {
    if (!deadline) return 'N/A';
    const now = new Date();
    const sla = new Date(deadline);
    const diff = sla - now;
    if (diff < 0) return 'Overdue';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || order.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleView = (order) => {
    setSelectedOrder(order);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>);

  }

  const stats = {
    total: orders.length,
    sameDay: orders.filter((o) => o.priority === 'SAME_DAY').length,
    inProgress: orders.filter((o) => !['DELIVERED', 'DISPATCHED'].includes(o.status)).length,
    dispatched: orders.filter((o) => o.status === 'DISPATCHED').length
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="rush-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("rushOrders", "Rush Orders")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("priorityOrdersWith", "Priority orders with expedited SLA requirements")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("totalRushOrders", "Total Rush Orders")}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border-2 border-red-200 p-4 bg-red-50">
          <div className="text-sm text-red-600">{t("sameDay", "Same Day")}</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{stats.sameDay}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("inProgress", "In Progress")}</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{stats.inProgress}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("dispatched", "Dispatched")}</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{stats.dispatched}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search rush orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9" />
            
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses", "All Statuses")}</SelectItem>
              {rushStatuses.map((s) =>
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allPriorities", "All Priorities")}</SelectItem>
              {rushPriorities.map((p) =>
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Order Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredOrders.map((order) =>
        <RushCard key={order.id} order={order} onView={handleView} />
        )}
        {filteredOrders.length === 0 &&
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900">{t("noRushOrders", "No rush orders found")}</h3>
          </div>
        }
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedOrder?.id}
              {selectedOrder &&
              <Badge className={cn(priorityConfig[selectedOrder.priority]?.color)}>
                  {priorityConfig[selectedOrder.priority]?.label}
                </Badge>
              }
            </DialogTitle>
            <DialogDescription>{selectedOrder?.customer}</DialogDescription>
          </DialogHeader>
          {selectedOrder &&
          <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <span className="text-red-600">{t("slaDeadline", "SLA Deadline")}</span>
                  <div className="font-bold text-red-700 mt-1 text-lg">
                    {selectedOrder.slaRemaining}
                  </div>
                  <div className="text-xs text-red-500 mt-0.5">
                    {new Date(selectedOrder.slaDeadline).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">{t("reason", "Reason")}</span>
                  <div className="font-medium mt-1">{selectedOrder.reason}</div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">{t("item", "Item")}</th>
                      <th className="text-center p-3">{t("location", "Location")}</th>
                      <th className="text-center p-3">{t("qty", "Qty")}</th>
                      <th className="text-center p-3">{t("picked", "Picked")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item) =>
                  <tr key={item.id} className="border-t">
                        <td className="p-3">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.sku}</div>
                        </td>
                        <td className="p-3 text-center font-mono text-sm">{item.location}</td>
                        <td className="p-3 text-center">{item.qty}</td>
                        <td className="p-3 text-center">
                          <span className={cn(
                        "font-medium",
                        item.picked === item.qty ? "text-emerald-600" : "text-amber-600"
                      )}>
                            {item.picked || 0}
                          </span>
                        </td>
                      </tr>
                  )}
                  </tbody>
                </table>
              </div>

              {selectedOrder.notifications &&
            <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm text-gray-500">{t("notificationsSent", "Notifications Sent:")}</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedOrder.notifications.map((notif, idx) =>
                <Badge key={idx} variant="outline" className="text-xs">
                        {notif}
                      </Badge>
                )}
                  </div>
                </div>
            }
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};